import { Dialog } from '@angular/cdk/dialog';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { trustedStepReturn } from '@app/shared/workflow-navigation/workflow-step-return';
import { ButtonCellRenderer } from '@app/shared/components/tables/ag-grid-cells/button-cell-renderer/button-cell-renderer.component';
import { GlobalSearchService } from '@app/shared/services/global-search.service';
import { RoleService } from '@app/shared/services/roles.service';
import { ToastService } from '@app/shared/services/toast.service';
import { createTerminologyUsageSignals } from '@app/shared/terminology/terminology-usage-signals';
import {
  formatPersonNameLastFirst,
  personFirstNames,
  personLastName,
  personLastNameKey,
} from '@app/shared/util/person-name';
import {
  createCanMutateHearingsSignal,
  PLATFORM_ADMIN_ROLE,
} from './scheduling-access';
import { EzDialogComponent } from '@template-ui/components/ez-dialog/ez-dialog.component';
import { UiLibraryModule } from '@template-ui/template-ui.module';
import { ButtonConfig, EzDialogData } from '@template-ui/types';
import { ColDef } from 'ag-grid-community';
import {
  CalendarA11y,
  CalendarDateFormatter,
  CalendarEvent,
  CalendarEventTimesChangedEvent,
  CalendarEventTitleFormatter,
  CalendarModule,
  CalendarUtils,
  CalendarView,
  DateAdapter,
} from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import {
  addDays,
  addMonths,
  addWeeks,
  format,
  isSameDay,
  isWeekend,
  startOfDay,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';
import { DatePickerModule } from 'primeng/datepicker';
import { catchError, of } from 'rxjs';
import {
  CalendarRange,
  DEFAULT_HEARING_SCHEDULE_SORT,
  HearingScheduleDay,
  HearingScheduleDisplayError,
  HearingScheduleQuery,
  HearingScheduleSort,
  HearingScheduleSortKey,
  ProceedingCalendarRow,
  ReadyToScheduleRow,
  RESCHEDULE_BLOCKED_TOAST_TITLE as RESCHEDULE_BLOCKED_TITLE,
  SCHEDULE_BLOCKED_TOAST_TITLE as SCHEDULE_BLOCKED_TITLE,
  ScheduleHearingRequest,
  ScheduleHearingResult,
  dayLabel,
  dayRange,
  describeHttpFailure,
  formatResource,
  formatScheduledTime,
  gridEndHour,
  gridStartHour,
  groupHearingsByDate,
  isInDay,
  isInMonth,
  isInWeek,
  mapHearingScheduleError,
  monthLabel,
  monthRange,
  needsHearingInput,
  NEEDS_HEARING_INPUT_HINT,
  NEEDS_HEARING_INPUT_LABEL,
  weekLabel,
  readinessColor,
  readinessOf,
  sortHearings,
  toCalendarEvents,
  weekRange,
} from './hearing-schedule.models';
import { HearingScheduleService } from './hearing-schedule.service';
import {
  resolveFirstAvailableDate,
  resolveScheduleStartDefault,
  SCHEDULE_MIN_LEAD_TIME_DAYS,
  schedulingDueDate,
  schedulingDueDay,
} from './first-available-date';
import { hearingPhaseCommands } from '../navigation/phms-detail-navigation';
import { PhmsDetailNavigationService } from '../navigation/phms-detail-navigation.service';
import {
  HearingDetailFlyoutComponent,
  MoveDateRequest,
} from '../hearing-detail/hearing-detail-flyout.component';
import {
  AssignableOfficial,
  HearingOfficialAssignmentService,
} from '../hearing-detail/hearing-official-assignment.service';
import { PHMS_SUBJECT_TERMINOLOGY } from '../phms-subject.terminology';
import { PHMS_FACILITY_TERMINOLOGY } from '../phms-facility.terminology';
import { PHMS_HEARING_TERMINOLOGY } from '../phms-hearing.terminology';
import { PHMS_OPERATIONAL_TERMINOLOGY } from '../phms-operational.terminology';
import { HearingScheduleSearchContext } from './hearing-schedule-search.context';
import {
  RescheduleHearingDialogComponent,
  RescheduleHearingDialogData,
} from './reschedule-hearing-dialog.component';
import {
  ScheduleHearingDialogComponent,
  ScheduleHearingDialogData,
} from './schedule-hearing-dialog.component';

interface SortOption {
  key: HearingScheduleSortKey;
  label: string;
}

/**
 * Client mirror of the server's minimum scheduling lead time
 * (CoreContextEzWorkflowRuntimeService.MinimumScheduleLeadTimeDays, ADO 1483). The drop/drag guards
 * pre-check it so a placement inside the window is explained up front with the same wording family;
 * the server stays the authoritative backstop for anything typed inside the dialogs.
 */

// Toast titles for the drag/drop scheduling guards (past-date, 15-day lead time, weekend) come from
// the shared constants (SCHEDULE_BLOCKED_TOAST_TITLE / RESCHEDULE_BLOCKED_TOAST_TITLE, imported
// above) so the drop-path toasts, the Schedule dialog, and the Reschedule dialog all raise the SAME
// titled notification. These rejections surface as toast popups so the block is unmissable at the
// drop point; the persistent #1442 preselect explanation stays in the inline schedule notice.

/**
 * Deep-link preselect request from the dashboard's row-level Schedule action (#1442), parsed from
 * the `?scheduleFor=<incarceratedPersonId>` query param. The optional display name and case-stage
 * label ride along (`scheduleForName` / `scheduleForStage`) so a not-ready inmate can be explained
 * with context the scheduling projections do not carry.
 */
interface SchedulePreselectRequest {
  incarceratedPersonId: number;
  name: string | null;
  stage: string | null;
}

const sortOptions = (statusLabel: string): readonly SortOption[] => [
  { key: 'dateTime', label: 'Date / Time' },
  { key: 'location', label: 'Location' },
  { key: 'resource', label: 'Resource' },
  { key: 'status', label: statusLabel },
];

export type HearingScheduleViewMode = 'calendar' | 'grid';

/** Escapes display text before embedding it in an EZ dialog question, which renders as HTML. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

@Component({
  selector: 'phms-hearing-schedule',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    UiLibraryModule,
    CalendarModule,
    DatePickerModule,
    DragDropModule,
    HearingDetailFlyoutComponent,
    RouterLink,
  ],
  // angular-calendar's view components inject these services, normally wired by
  // CalendarModule.forRoot(); for a standalone component we provide them directly
  // (forRoot() returns a ModuleWithProviders that isn't valid in standalone imports).
  providers: [
    { provide: DateAdapter, useFactory: adapterFactory },
    CalendarEventTitleFormatter,
    CalendarDateFormatter,
    CalendarUtils,
    CalendarA11y,
  ],
  templateUrl: './hearing-schedule.component.html',
  styleUrl: './hearing-schedule.component.scss',
})
export class HearingScheduleComponent {
  readonly subjectText = createTerminologyUsageSignals(
    PHMS_SUBJECT_TERMINOLOGY.hearingSchedule,
  );
  readonly facilityText = createTerminologyUsageSignals(
    PHMS_FACILITY_TERMINOLOGY.hearingSchedule,
  );
  readonly hearingText = createTerminologyUsageSignals(
    PHMS_HEARING_TERMINOLOGY.hearingSchedule,
  );
  readonly operationalText = createTerminologyUsageSignals(
    PHMS_OPERATIONAL_TERMINOLOGY.hearingSchedule,
  );
  // Re-exposed so the template can switch the angular-calendar view component.
  readonly CalendarView = CalendarView;
  private readonly destroyRef = inject(DestroyRef);
  private readonly scheduleService = inject(HearingScheduleService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  workflowReturnRoute(): string | null {
    return trustedStepReturn(
      this.route.snapshot.queryParamMap.get('workflowReturnTo'),
    );
  }
  private readonly roles = inject(RoleService);
  private readonly detailNavigation = inject(PhmsDetailNavigationService);
  private readonly globalSearch = inject(GlobalSearchService);
  private readonly dialog = inject(Dialog);
  /** Hosts the EZ confirmation dialog behind the list-card Unschedule (#1526). */
  private readonly confirmDialog = inject(MatDialog);
  private readonly toast = inject(ToastService);
  private readonly officialAssignments = inject(
    HearingOfficialAssignmentService,
  );

  // Platform admins administer the calendar regardless of the scheduling-mutate permission, so the
  // move/remove controls are also offered when the user holds the platform-admin role.
  private readonly isPlatformAdmin =
    this.roles.hasAnyRoleSignal(PLATFORM_ADMIN_ROLE);

  /**
   * Whether the calendar's add/remove/reschedule controls are permitted for the
   * current effective permissions (PHMS-SCH-6/#PHMS-SCH-7 bind add/remove/reschedule
   * to this). Built by the shared scheduling gate (scheduling-access.ts) — the same signal
   * source the dashboard's row-level Schedule action uses (#1442) — so the two surfaces
   * can never drift. Server authorization on workflow_runtime.transition stays
   * authoritative; this is UX-only.
   */
  readonly canMutateHearings = createCanMutateHearingsSignal(this.roles);

  /**
   * Hide the scheduling work queue for read-only parole officials. Scheduling authority takes
   * precedence when a user also has scheduling permissions or the platform-admin role.
   * The backlog still loads for search eligibility annotations and deep-link resolution.
   */
  private readonly isParoleOfficial =
    this.roles.hasAnyRoleSignal('parole_official');
  readonly hidesAwaitingScheduling = computed(
    () => this.isParoleOfficial() && !this.canMutateHearings(),
  );

  private readonly hasOfficialAssignPermission =
    this.roles.hasAnyPermissionSignal(OFFICIAL_ASSIGN_PERMISSION);

  /**
   * Whether the flyout's Assign/Change-official controls are permitted (story 1444). True when the
   * user carries workflow_runtime.assign (schedulers receive it with #1444) or holds the
   * platform-admin role. Server authorization on workflow_runtime.assign stays authoritative; this
   * is UX-only.
   */
  readonly canAssignOfficials = computed(
    () => this.hasOfficialAssignPermission() || this.isPlatformAdmin(),
  );

  /**
   * Hour window for the Week/Day time grids: 9am–9pm, zoomed out (hourSegmentHeight 20) so the
   * whole window is visible without scrolling (angular-calendar renders the full 24h by default,
   * which forces a tall, scrolling grid).
   * `dayStartHour` is also the DEFAULT start hour prefilled into the Schedule Hearing confirmation
   * dialog for a day-picked/dropped hearing (scheduleReadyCandidate); the scheduler edits the
   * actual time in the dialog before confirming.
   * The grids themselves bind gridDayStartHour/gridDayEndHour below, which widen this window when a
   * hearing falls outside it so no event is ever silently clipped from the Week/Day views.
   */
  readonly dayStartHour = 9;
  readonly dayEndHour = 21;

  readonly viewMode = signal<HearingScheduleViewMode>('calendar');
  // When viewMode() === "calendar" this selects the angular-calendar view component. Week is the
  // default landing view: schedulers work the current week first; Month/Day stay one click away.
  readonly calendarView = signal<CalendarView>(CalendarView.Week);
  readonly viewDate = signal<Date>(new Date());

  /** Month/year picker (opened by clicking the period label in the header nav). */
  readonly showMonthPicker = signal(false);
  readonly pickerYear = signal<number>(new Date().getFullYear());
  readonly pickerMonths: readonly string[] = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  readonly isLoading = signal(true);
  readonly errorState = signal<HearingScheduleDisplayError | null>(null);
  readonly hearings = signal<ProceedingCalendarRow[]>([]);
  readonly sort = signal<HearingScheduleSort>({
    ...DEFAULT_HEARING_SCHEDULE_SORT,
  });

  /**
   * Timestamp of the last successful calendar fetch (#1441). Backs the "Updated h:mm" note beside
   * the toolbar's Refresh Schedule button so a manual refresh gives visible success feedback; a
   * failed refresh leaves it unchanged (the error state renders instead).
   */
  readonly lastUpdatedAt = signal<Date | null>(null);

  /** Short time label ("3:42 PM") for the last successful refresh, or null before the first load lands. */
  readonly lastUpdatedLabel = computed(() => {
    const updated = this.lastUpdatedAt();
    return updated
      ? new Intl.DateTimeFormat(undefined, { timeStyle: 'short' }).format(
          updated,
        )
      : null;
  });

  /**
   * Ready-to-schedule backlog (proceedings eligible to place, hence not yet on the calendar). Drives
   * the top-nav search's per-result "Add to Calendar" action via the registered search context AND
   * the eligible-inmates strip rendered above the calendar.
   */
  private readonly eligibleBacklog = signal<ReadyToScheduleRow[]>([]);

  /**
   * Facility filter (#1458): "all" shows every location's schedule; a location name narrows BOTH
   * the calendar events and the eligible strip to that facility. Client-side only — the loaded
   * hearings/backlog stay untouched, so switching back to "all" restores everything instantly.
   */
  readonly facilityFilter = signal<string>('all');

  /**
   * Facility options for the filter select: the distinct, sorted location names present in the
   * loaded hearings AND the eligible backlog (both surfaces the filter narrows). "All facilities"
   * is rendered as a fixed first option by the template, not carried here.
   */
  readonly facilityOptions = computed<string[]>(() => {
    const names = new Set<string>();
    for (const hearing of this.hearings()) {
      const name = hearing.locationName?.trim();
      if (name) {
        names.add(name);
      }
    }
    for (const candidate of this.eligibleBacklog()) {
      const name = candidate.locationName?.trim();
      if (name) {
        names.add(name);
      }
    }
    return [...names].sort((left, right) => left.localeCompare(right));
  });

  setFacilityFilter(facility: string): void {
    this.facilityFilter.set(facility?.trim() || 'all');
  }

  /** Rows matching the active facility filter; every row when the filter is "all". */
  private matchesFacility(locationName: string | null | undefined): boolean {
    const facility = this.facilityFilter();
    return facility === 'all' || (locationName ?? '').trim() === facility;
  }

  /**
   * Commissioner filter (#1531): "all" shows every official's hearings; a selection narrows the
   * calendar to the hearings that official is assigned to. Keyed on the official's USER ID, not the
   * display name — two officials can share a name, and keying on the name would merge their
   * calendars. Client-side like the facility filter: the loaded window is untouched, so returning to
   * "all" restores every hearing instantly.
   */
  readonly officialFilter = signal<string>('all');

  /** Assignable officials (active parole_official users) backing the filter's option list. */
  readonly officialOptions = signal<AssignableOfficial[]>([]);

  setOfficialFilter(officialUserId: string): void {
    this.officialFilter.set(officialUserId?.trim() || 'all');
  }

  /** Rows matching the active commissioner filter; every row when the filter is "all". */
  private matchesOfficial(
    assignedOfficialUserId: number | null | undefined,
  ): boolean {
    const official = this.officialFilter();
    if (official === 'all') {
      return true;
    }
    return (
      assignedOfficialUserId != null &&
      String(assignedOfficialUserId) === official
    );
  }

  /**
   * Eligible-inmates strip above the calendar: the same ready-to-schedule backlog the search tray
   * uses, ordered by last name (#1436 ordering), narrowed to the selected facility (#1458).
   * Visible to every viewer; only the drag-to-schedule affordance is gated on canMutateHearings.
   */
  readonly eligibleStripCandidates = computed<ReadyToScheduleRow[]>(() =>
    this.eligibleBacklog()
      .filter((candidate) => this.matchesFacility(candidate.locationName))
      .sort((left, right) =>
        personLastNameKey(left.title).localeCompare(
          personLastNameKey(right.title),
        ),
      ),
  );
  // Eligible candidates keyed by inmate id (ReadyToScheduleRow.subjectKey holds the incarcerated person
  // id for hearing_case subjects), for O(1) lookup against a global-search result.
  private readonly eligibleByInmate = computed<Map<string, ReadyToScheduleRow>>(
    () => {
      const byInmate = new Map<string, ReadyToScheduleRow>();
      for (const candidate of this.eligibleBacklog()) {
        const key = (candidate.subjectKey ?? '').trim();
        if (key && !byInmate.has(key)) {
          byInmate.set(key, candidate);
        }
      }
      return byInmate;
    },
  );

  /**
   * Future scheduled hearings across a wide forward window (not just the visible month), keyed by
   * incarcerated-person id (calendar row subjectKey). Backs the top-nav search's "View in Calendar"
   * action so it can jump to a scheduled inmate's hearing month regardless of the current view.
   */
  private readonly futureScheduled = signal<ProceedingCalendarRow[]>([]);
  private readonly futureScheduledByInmate = computed<
    Map<string, ProceedingCalendarRow>
  >(() => {
    const byInmate = new Map<string, ProceedingCalendarRow>();
    for (const row of this.futureScheduled()) {
      const key = (row.subjectKey ?? '').trim();
      if (!key || !row.scheduledStartUtc) {
        continue;
      }
      const existing = byInmate.get(key);
      // Keep the earliest upcoming hearing when an inmate has more than one scheduled.
      if (
        !existing ||
        (existing.scheduledStartUtc ?? '') > row.scheduledStartUtc
      ) {
        byInmate.set(key, row);
      }
    }
    return byInmate;
  });

  // Facility-filtered (#1458) and commissioner-filtered (#1531) before sorting: every downstream
  // surface (calendar events, List groups, day/week/month grids, per-view summaries) reads
  // visibleHearings, so one narrowing covers them all and the views cannot disagree. The two filters
  // compose — selecting both shows the intersection.
  readonly visibleHearings = computed<ProceedingCalendarRow[]>(() =>
    sortHearings(
      this.hearings().filter(
        (hearing) =>
          this.matchesFacility(hearing.locationName) &&
          this.matchesOfficial(hearing.assignedOfficialUserId),
      ),
      this.sort(),
    ),
  );
  readonly days = computed<HearingScheduleDay[]>(() =>
    groupHearingsByDate(this.visibleHearings()),
  );
  readonly hasHearings = computed(() => this.visibleHearings().length > 0);

  // angular-calendar events for the visible (filtered/sorted) rows. Unscheduled
  // rows are skipped by toCalendarEvents. This is the ONE row→event mapping every calendar view
  // (Month/Week/Day) renders from, so a hearing can never appear in one view but not another.
  // `draggable` powers angular-calendar's native drag on the Week/Day time grids (#1458) — the
  // Month view ignores it because the custom cell template renders CDK-drag chips instead.
  readonly events = computed<CalendarEvent<ProceedingCalendarRow>[]>(() => {
    const draggable = this.canMutateHearings();
    return toCalendarEvents(this.visibleHearings()).map((event) => ({
      ...event,
      draggable,
    }));
  });

  /**
   * Hour window the Week/Day time grids display. Defaults to business hours (9–17) but widens to
   * cover any visible event: angular-calendar drops events entirely outside the displayed window
   * from the Week/Day grids while the Month view always shows them, so a fixed window let early or
   * late hearings appear in Month but vanish from Week/Day for the very same date.
   */
  readonly gridDayStartHour = computed(() =>
    gridStartHour(this.events(), this.dayStartHour),
  );
  readonly gridDayEndHour = computed(() =>
    gridEndHour(this.events(), this.dayEndHour),
  );

  // Month-view header summary. Counts only the hearings whose scheduled start
  // falls within the calendar month of `viewDate` (the padded leading/trailing
  // days that the grid borrows from adjacent months are excluded), so the
  // "N hearings / N Ready" line reflects the month the label names. "Ready" uses
  // the same readiness classification that colors the chips, keeping them
  // consistent.
  readonly monthLabel = computed(() => monthLabel(this.viewDate()));
  private readonly monthHearings = computed<ProceedingCalendarRow[]>(() => {
    const date = this.viewDate();
    return this.visibleHearings().filter((row) =>
      isInMonth(row.scheduledStartUtc, date),
    );
  });
  readonly monthHearingCount = computed(() => this.monthHearings().length);
  readonly monthReadyCount = computed(
    () =>
      this.monthHearings().filter((row) => readinessOf(row) === 'ready').length,
  );

  // Week-view header summary. Mirrors the Month summary but scoped to the
  // startOfWeek…endOfWeek window of `viewDate` (the week the label names).
  readonly weekLabel = computed(() => weekLabel(this.viewDate()));
  private readonly weekHearings = computed<ProceedingCalendarRow[]>(() => {
    const date = this.viewDate();
    return this.visibleHearings().filter((row) =>
      isInWeek(row.scheduledStartUtc, date),
    );
  });
  readonly weekHearingCount = computed(() => this.weekHearings().length);
  readonly weekReadyCount = computed(
    () =>
      this.weekHearings().filter((row) => readinessOf(row) === 'ready').length,
  );

  // Day-view header summary. Mirrors the Week summary but scoped to the
  // startOfDay…endOfDay window of `viewDate` (the day the label names).
  readonly dayLabel = computed(() => dayLabel(this.viewDate()));
  private readonly dayHearings = computed<ProceedingCalendarRow[]>(() => {
    const date = this.viewDate();
    return this.visibleHearings().filter((row) =>
      isInDay(row.scheduledStartUtc, date),
    );
  });
  readonly dayHearingCount = computed(() => this.dayHearings().length);
  readonly dayReadyCount = computed(
    () =>
      this.dayHearings().filter((row) => readinessOf(row) === 'ready').length,
  );

  /** Label for the active calendar view (month/week/day), shown in the header nav. */
  readonly periodLabel = computed(() => {
    switch (this.calendarView()) {
      case CalendarView.Week:
        return this.weekLabel();
      case CalendarView.Day:
        return this.dayLabel();
      default:
        return this.monthLabel();
    }
  });

  // Header live count.
  readonly totalScheduled = computed(() => this.visibleHearings().length);

  readonly columnDefs = computed<ColDef[]>(() => this.buildColumnDefs());

  readonly sortButtons = computed<ButtonConfig[]>(() =>
    sortOptions(this.operationalText.statusColumnPicker()).map((option) => {
      const active = this.sort().key === option.key;
      const arrow = active
        ? this.sort().direction === 'asc'
          ? ' ↑'
          : ' ↓'
        : '';
      return {
        id: `sort:${option.key}`,
        buttonText: `${option.label}${arrow}`,
        severity: active ? 'primary' : 'secondary',
      } satisfies ButtonConfig;
    }),
  );

  readonly pageTitle = 'Scheduling';
  // Live header line: "Upcoming parole hearings, N total scheduled".
  readonly pageSubtitle = computed(
    () => `Upcoming parole hearings, ${this.totalScheduled()} total scheduled`,
  );

  readonly formatScheduledTime = formatScheduledTime;
  readonly formatResource = formatResource;
  readonly readinessColor = readinessColor;
  readonly readinessOf = readinessOf;
  /** Renders inmate names as "Last, First Middle" (#1436); the raw title stays on the row. */
  readonly formatPersonName = formatPersonNameLastFirst;
  /** Split name renders for the eligible grid's Last Name / First Name columns; raw title stays on the row. */
  readonly personLastName = personLastName;
  readonly personFirstNames = personFirstNames;
  /** Overdue "awaiting hearing input" state (#1447): server-derived flag on the calendar row. */
  readonly needsHearingInput = needsHearingInput;
  readonly needsHearingInputLabel = NEEDS_HEARING_INPUT_LABEL;
  readonly needsHearingInputHint = NEEDS_HEARING_INPUT_HINT;

  /**
   * Pending dashboard-to-scheduling preselect (#1442): parsed from `?scheduleFor` at construction
   * and resolved once BOTH the eligible backlog and the forward scheduled index have loaded, so the
   * chosen inmate can be classified as schedulable / already scheduled / not ready. Cleared after
   * the first resolution so later backlog refreshes never re-trigger it.
   */
  readonly schedulingChoices = signal<{ id: number; label: string; scheduled: boolean }[]>([]);
  chooseSchedulingHearing(value: string): void {
    const selected = this.schedulingChoices().find(c => c.id === Number(value));
    if (!selected) return;
    this.schedulingChoices.set([]); this.scheduleNotice.set(null);
    if (selected.scheduled) {
      const row = this.futureScheduled().find(r => r.scheduledProceedingId === selected.id);
      if (row?.scheduledStartUtc) { this.setViewDate(new Date(row.scheduledStartUtc)); this.openHearing(row); }
    } else {
      const row = this.eligibleBacklog().find(r => r.scheduledProceedingId === selected.id);
      if (row) this.onSearchAdd(row);
    }
  }
  private resolvedSchedulingRequest = false;
  private pendingSchedulePreselect: SchedulePreselectRequest | null = null;
  /** Signal (not a plain flag) so the eligible strip can show a quiet loading state on first paint. */
  readonly eligibleBacklogLoaded = signal(false);
  private futureScheduledLoaded = false;

  /**
   * Diagnosable backlog-load failure, rendered inside the eligible strip. Null while the backlog
   * loads cleanly; a failed fetch fills it with the HTTP status + server problem title so the
   * strip never silently masquerades a server fault as "No inmates awaiting scheduling".
   */
  readonly eligibleBacklogError = signal<string | null>(null);

  /**
   * Dismissible explanation shown when a dashboard Schedule deep-link cannot open the scheduling
   * flow directly (#1442 AC4): the inmate is not in the ready-to-schedule backlog yet, is already
   * on the calendar, or the user lacks the scheduling permission. Null when no notice is shown;
   * the page stays fully usable underneath.
   */
  readonly scheduleNotice = signal<string | null>(null);

  constructor() {
    this.applyDevRoleQueryParam();
    // Capture the dashboard deep-link before the loads run: with synchronous test observables the
    // load subscriptions (and therefore the preselect resolution) fire inside this constructor.
    this.pendingSchedulePreselect = this.readSchedulePreselectRequest();
    this.openFlyoutFromDeepLink();
    this.load();
    this.loadEligibleBacklog();
    this.loadFutureScheduledIndex();
    this.loadOfficialOptions();
    this.registerSearchContext();
  }

  /**
   * Officials backing the commissioner filter (#1531), from the read-gated officials route the assign
   * picker already uses. A failed load leaves the option list empty so the filter degrades to its
   * all-officials default rather than taking the page down with it — the calendar itself does not
   * depend on this list.
   */
  private loadOfficialOptions(): void {
    this.officialAssignments
      .getOfficials()
      .pipe(
        catchError(() => of<AssignableOfficial[]>([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((officials) => this.officialOptions.set(officials ?? []));
  }

  /**
   * Repoint the top-nav inmate search at this page while it is mounted: results that are eligible to
   * schedule gain an "Add to Calendar" action wired to onSearchAdd. Cleared on destroy so other pages
   * keep the default "Open" behavior. Registered unconditionally — the backlog is empty (and the Add
   * button hidden) for users who cannot read ready-to-schedule, and onSearchAdd re-checks the gate.
   */
  private registerSearchContext(): void {
    const context = new HearingScheduleSearchContext(
      (inmateId) => this.findScheduleCandidate(inmateId),
      (candidate) => this.onSearchAdd(candidate),
      (inmateId) => this.findScheduledHearing(inmateId),
      (hearing) => this.onSearchViewMonth(hearing),
    );
    this.globalSearch.setContext(context);
    this.destroyRef.onDestroy(() => this.globalSearch.clearContext(context));
  }

  private loadEligibleBacklog(): void {
    this.eligibleBacklogError.set(null);
    this.scheduleService
      .getReadyToSchedule()
      .pipe(
        catchError((error: unknown) => {
          // Surface the failure in the strip instead of silently rendering an empty backlog: a
          // ready-to-schedule 500 used to be indistinguishable from "no inmates awaiting
          // scheduling", hiding server faults entirely. The HTTP status + problem title makes the
          // next occurrence self-diagnosing.
          this.eligibleBacklogError.set(
            'The eligible-to-schedule list could not be loaded (' +
              `${error instanceof HttpErrorResponse ? describeHttpFailure(error) : 'unexpected error'}). ` +
              'Refresh the page or contact support if the problem persists.',
          );
          return of<ReadyToScheduleRow[]>([]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((candidates) => {
        this.eligibleBacklog.set(candidates ?? []);
        this.eligibleBacklogLoaded.set(true);
        this.tryResolveSchedulePreselect();
      });
  }

  /**
   * Loads the forward window of scheduled hearings (now → ~18 months) used by the search's "View in
   * Calendar" action. Independent of the visible-month fetch so the jump works for any future month.
   */
  private loadFutureScheduledIndex(): void {
    const now = new Date();
    const end = new Date(now.getTime());
    end.setMonth(end.getMonth() + 18);
    this.scheduleService
      .getCalendar({ startUtc: now.toISOString(), endUtc: end.toISOString() })
      .pipe(
        catchError(() => of<ProceedingCalendarRow[]>([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((rows) => {
        this.futureScheduled.set(rows ?? []);
        this.futureScheduledLoaded = true;
        this.tryResolveSchedulePreselect();
      });
  }

  /**
   * Dashboard hearing deep-link (#1458): `?openDetail=<scheduledProceedingId>` opens this page
   * with the hearing-detail flyout already showing. The role dashboards' Open / Hearing # actions
   * land here so every role gets the same full hearing-detail experience the schedule page's
   * calendar drill-in provides — and the input phases stay one click away via the flyout's
   * View Full Hearing action.
   */
  private openFlyoutFromDeepLink(): void {
    const proceedingId = Number.parseInt(
      this.route.snapshot.queryParamMap.get('openDetail') ?? '',
      10,
    );
    if (Number.isInteger(proceedingId) && proceedingId > 0) {
      this.flyoutProceedingId.set(proceedingId);
    }
  }

  /**
   * Parses the dashboard Schedule deep-link query params (#1442). Null when no valid
   * `?scheduleFor=<incarceratedPersonId>` is present, which disables the preselect entirely.
   */
  private readSchedulePreselectRequest(): SchedulePreselectRequest | null {
    const params = this.route.snapshot.queryParamMap;
    const incarceratedPersonId = Number.parseInt(
      params.get('scheduleFor') ?? '',
      10,
    );
    if (!Number.isInteger(incarceratedPersonId) || incarceratedPersonId <= 0) {
      return null;
    }
    return {
      incarceratedPersonId,
      name: params.get('scheduleForName')?.trim() || null,
      stage: params.get('scheduleForStage')?.trim() || null,
    };
  }

  /**
   * Resolves the pending dashboard preselect once both lookup indexes (eligible backlog + forward
   * scheduled window) have loaded. Runs at most once per page init: the request is cleared before
   * resolving so backlog refreshes after a schedule write never replay it.
   */
  private tryResolveSchedulePreselect(): void {
    const requested = this.route.snapshot.queryParamMap.get('schedulingRequestId');
    if (requested) {
      if(this.resolvedSchedulingRequest) return;
      if (!this.eligibleBacklogLoaded() || !this.futureScheduledLoaded) return;
      const id = Number(requested);
      this.resolvedSchedulingRequest = true;
      const candidate = this.eligibleBacklog().find(r => r.schedulingContext?.requestId === id);
      if (candidate) this.onSearchAdd(candidate);
      else {
        const booked = this.futureScheduled().find(r => r.schedulingContext?.requestId === id);
        if (booked?.scheduledStartUtc) { this.setViewDate(new Date(booked.scheduledStartUtc)); this.openHearing(booked); }
        else this.scheduleNotice.set('This exact hearing request is not currently available for scheduling.');
      }
      this.pendingSchedulePreselect = null;
      return;
    }
    const request = this.pendingSchedulePreselect;
    if (
      !request ||
      !this.eligibleBacklogLoaded() ||
      !this.futureScheduledLoaded
    ) {
      return;
    }
    this.pendingSchedulePreselect = null;
    this.resolveSchedulePreselect(request);
  }

  /**
   * Dashboard-to-scheduling preselect (#1442). A schedulable inmate goes straight into the existing
   * Add-to-Calendar flow with that inmate preselected (AC2); every other outcome gets a clear,
   * dismissible explanation instead of a dead end (AC4): an already-scheduled inmate also recenters
   * the calendar on their hearing, and a not-ready inmate surfaces the current case stage when the
   * dashboard passed it along.
   */
  private resolveSchedulePreselect(request: SchedulePreselectRequest): void {
    const fallbackName =
      formatPersonNameLastFirst(request.name) || this.subjectText.thisSubject();

    const readyMatches = this.eligibleBacklog().filter(r => r.subjectKey === String(request.incarceratedPersonId));
    const bookedMatches = this.futureScheduled().filter(r => r.subjectKey === String(request.incarceratedPersonId));
    const choices = [...readyMatches.map(r => ({id:r.scheduledProceedingId,label:r.proceedingNumber + ' · Awaiting scheduling',scheduled:false})),
      ...bookedMatches.map(r => ({id:r.scheduledProceedingId,label:r.proceedingNumber + ' · ' + (r.scheduledStartUtc || 'Booked'),scheduled:true}))];
    if (new Set(choices.map(c => c.id)).size > 1) {
      this.schedulingChoices.set(choices); this.scheduleNotice.set('Choose the hearing you want to open.'); return;
    }
    const candidate = this.findScheduleCandidate(request.incarceratedPersonId);
    if (candidate) {
      if (!this.canMutateHearings()) {
        // The dashboard hides the Schedule action without the permission (AC3); explain rather
        // than silently no-op if a crafted or stale link lands here anyway.
        const name = formatPersonNameLastFirst(candidate.title) || fallbackName;
        this.scheduleNotice.set(
          `${name} is ready to schedule, but you do not have permission to schedule hearings. ` +
            this.hearingText.askCoordinator(),
        );
        return;
      }
      this.onSearchAdd(candidate);
      return;
    }

    const scheduled = this.findScheduledHearing(request.incarceratedPersonId);
    if (scheduled) {
      const name = formatPersonNameLastFirst(scheduled.title) || fallbackName;
      this.scheduleNotice.set(
        `${name} already has a hearing scheduled for ` +
          `${formatDateTimeLabel(scheduled.scheduledStartUtc)}; the calendar is centered on it.`,
      );
      this.onSearchViewMonth(scheduled);
      return;
    }

    const stage = request.stage ? ` — current stage: ${request.stage}` : '';
    this.scheduleNotice.set(
      `${fallbackName} is not ready to schedule yet${stage}. ` +
        this.subjectText.eligibilityExplanation(),
    );
  }

  /** Dismisses the deep-link explanation notice (#1442 AC4); the page stays fully usable. */
  dismissScheduleNotice(): void {
    this.scheduleNotice.set(null);
  }

  /** The chosen inmate's earliest upcoming scheduled hearing, or undefined when they have none. */
  private findScheduledHearing(
    incarceratedPersonId: number,
  ): ProceedingCalendarRow | undefined {
    if (!(incarceratedPersonId > 0)) {
      return undefined;
    }
    return this.futureScheduledByInmate().get(String(incarceratedPersonId));
  }

  /**
   * Search "View in Calendar": switch to the month calendar centered on the inmate's scheduled hearing
   * so the user lands on the month where that hearing sits. setViewDate refetches the visible window.
   */
  onSearchViewMonth(hearing: ProceedingCalendarRow): void {
    const start = hearing?.scheduledStartUtc
      ? new Date(hearing.scheduledStartUtc)
      : null;
    if (!start || Number.isNaN(start.getTime())) {
      return;
    }
    this.viewMode.set('calendar');
    this.calendarView.set(CalendarView.Week);
    this.setViewDate(start);
  }

  /** Eligible ready-to-schedule candidate for a global-search result, or undefined when not eligible. */
  private findScheduleCandidate(
    incarceratedPersonId: number,
  ): ReadyToScheduleRow | undefined {
    if (!(incarceratedPersonId > 0)) {
      return undefined;
    }
    const candidates = this.eligibleBacklog().filter(r => r.subjectKey === String(incarceratedPersonId));
    return candidates.length === 1 ? candidates[0] : undefined;
  }

  /**
   * Dev-mode effective-role wiring (PHMS-SCH-5). The role override has no on-page control; a developer
   * selects it via the `?devRole=commissioner|coordinator|admin` query param. The override setter is a
   * no-op unless the user is a developer on a non-production build, so this is inert in production and
   * for non-developers.
   */
  private applyDevRoleQueryParam(): void {
    const raw = this.route.snapshot.queryParamMap
      .get('devRole')
      ?.trim()
      .toLowerCase();
    if (raw === 'commissioner' || raw === 'coordinator' || raw === 'admin') {
      this.roles.setEffectiveRoleOverride(raw);
    }
  }

  setView(mode: HearingScheduleViewMode): void {
    this.viewMode.set(mode);
    // The grid/list path is range-agnostic; only the calendar drives a windowed fetch.
    if (mode === 'calendar') {
      this.load();
    }
  }

  setCalendarView(view: CalendarView): void {
    this.calendarView.set(view);
    this.load();
  }

  setViewDate(date: Date): void {
    this.viewDate.set(date);
    this.load();
  }

  /**
   * Month-view navigation. Shifts `viewDate` by one calendar month and refetches:
   * the new month's visible-grid range flows through `buildQuery()`/`currentRange()`
   * so the fresh window is requested from the calendar projection (AC #2).
   */
  prevMonth(): void {
    this.setViewDate(subMonths(this.viewDate(), 1));
  }

  nextMonth(): void {
    this.setViewDate(addMonths(this.viewDate(), 1));
  }

  /**
   * Week-view navigation. Shifts `viewDate` by one calendar week and refetches:
   * the new week's `weekRange` flows through `buildQuery()`/`currentRange()` so the
   * fresh window is requested from the calendar projection and the new week's
   * events render (AC #2). Mirrors prevMonth/nextMonth.
   */
  prevWeek(): void {
    this.setViewDate(subWeeks(this.viewDate(), 1));
  }

  nextWeek(): void {
    this.setViewDate(addWeeks(this.viewDate(), 1));
  }

  /**
   * Day-view navigation. Shifts `viewDate` by one calendar day and refetches:
   * the new day's `dayRange` flows through `buildQuery()`/`currentRange()` so the
   * fresh window is requested from the calendar projection and the new day's
   * events render (AC #2). Mirrors prevWeek/nextWeek.
   */
  prevDay(): void {
    this.setViewDate(subDays(this.viewDate(), 1));
  }

  nextDay(): void {
    this.setViewDate(addDays(this.viewDate(), 1));
  }

  /** Header nav: step the active calendar view one period back. */
  onPrevPeriod(): void {
    switch (this.calendarView()) {
      case CalendarView.Week:
        this.prevWeek();
        break;
      case CalendarView.Day:
        this.prevDay();
        break;
      default:
        this.prevMonth();
        break;
    }
  }

  /** Header nav: step the active calendar view one period forward. */
  onNextPeriod(): void {
    switch (this.calendarView()) {
      case CalendarView.Week:
        this.nextWeek();
        break;
      case CalendarView.Day:
        this.nextDay();
        break;
      default:
        this.nextMonth();
        break;
    }
  }

  /** Toggle the month/year picker, seeding the displayed year from the current view date. */
  toggleMonthPicker(): void {
    this.showMonthPicker.update((shown) => {
      const next = !shown;
      if (next) {
        this.pickerYear.set(this.viewDate().getFullYear());
      }
      return next;
    });
  }

  pickerPrevYear(): void {
    this.pickerYear.update((year) => year - 1);
  }

  pickerNextYear(): void {
    this.pickerYear.update((year) => year + 1);
  }

  /** Jump the calendar to the chosen month/year (switching to month view) and close the picker. */
  selectPickerMonth(monthIndex: number): void {
    this.calendarView.set(CalendarView.Month);
    this.setViewDate(new Date(this.pickerYear(), monthIndex, 1));
    this.showMonthPicker.set(false);
  }

  scheduleHearing(): void {
    // Placeholder destination until the schedule-hearing flow lands (later story).
    void this.router.navigate(['/phms/hearing-schedule/new']);
  }

  /**
   * Tracks an in-flight schedule/unschedule write so the UI can disable the controls and avoid
   * double-submits. Null when no mutation is pending.
   */
  readonly mutatingProceedingId = signal<number | null>(null);

  /**
   * Inmate-search Add-to-Calendar candidate awaiting a day pick. Set when the search tray's "Add to
   * Calendar" button fires; drives the inline date-picker dropdown. Null when the dropdown is closed.
   */
  readonly pendingAddCandidate = signal<ReadyToScheduleRow | null>(null);
  readonly addDateMaximum = computed(() => {
    const candidate = this.pendingAddCandidate();
    return candidate ? schedulingDueDay(candidate) ?? undefined : undefined;
  });

  /** Month the Add-to-Calendar date picker opens on (the candidate's default placement day). */
  readonly addDateDefault = signal<Date>(new Date());

  /**
   * The first available date the calendar positioned itself on (#1487), with the name it belongs to,
   * so the banner can explain WHY the view jumped. Null whenever the calendar is sitting wherever the
   * scheduler left it, which is every load that carries no scheduling action.
   */
  readonly firstAvailable = signal<{
    readonly date: Date;
    readonly name: string;
  } | null>(null);

  /** True for the day the calendar opened on, so the month grid can mark that cell. */
  isFirstAvailableDay(day: Date): boolean {
    const marked = this.firstAvailable()?.date;
    return !!marked && !!day && isSameDay(marked, day);
  }

  /** The scheduler has seen why the calendar moved; stop drawing attention to it. */
  dismissFirstAvailable(): void {
    this.firstAvailable.set(null);
  }

  /** Label for the banner, e.g. "Mon, Oct 5, 2026". */
  formatFirstAvailable(day: Date): string {
    return format(day, 'EEE, MMM d, yyyy');
  }

  /**
   * List-card Unschedule (#1526): confirms through the app's EZ dialog (the MatDialog-hosted
   * EzDialogComponent the hearing record uses) before routing to {@link removeHearing}, so the card
   * and the flyout both prompt before anything changes. Keep hearing, Escape and backdrop dismissal
   * all leave the hearing on the calendar.
   */
  confirmUnschedule(row: ProceedingCalendarRow): void {
    if (
      !row ||
      !this.canMutateHearings() ||
      this.mutatingProceedingId() !== null
    ) {
      return;
    }

    const name =
      formatPersonNameLastFirst(row.title) || this.hearingText.thisHearing();
    // EzDialog renders question text as HTML; the inmate name must remain plain text.
    const data: EzDialogData = {
      heading: this.hearingText.unscheduleConfirmTitle(),
      headerStyle: 'warning',
      question: `${escapeHtml(name)}: ${this.hearingText.unscheduleConfirmMessage()}`,
      buttons: [
        { text: this.hearingText.keep(), action: (ref) => ref.close(false) },
        {
          text: this.hearingText.confirmUnschedule(),
          action: (ref) => ref.close(true),
        },
      ],
    };
    this.confirmDialog
      .open(EzDialogComponent, {
        width: '38rem',
        maxWidth: 'calc(100vw - 2rem)',
        autoFocus: 'first-tabbable',
        restoreFocus: true,
        ariaLabel: data.heading,
        data,
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed === true) {
          this.removeHearing(row);
        }
      });
  }

  /**
   * Commissioner-only: remove (unschedule) a hearing from the calendar (PHMS-SCH-6, AC2). Server
   * authorization on workflow_runtime.transition is authoritative; the client gate is UX only, so
   * this no-ops when the gate is off. On success it reloads so the hearing disappears and the
   * counts recompute (AC4).
   */
  removeHearing(row: ProceedingCalendarRow): void {
    if (
      !row ||
      !this.canMutateHearings() ||
      this.mutatingProceedingId() !== null
    ) {
      return;
    }

    this.mutatingProceedingId.set(row.scheduledProceedingId);
    this.scheduleService
      .unscheduleHearing(row.scheduledProceedingId)
      .pipe(
        catchError((error: unknown) => {
          const mapped = mapHearingScheduleError(error);
          // A refused transition (workflow.proceeding_unschedule.*) carries issue codes; title the
          // banner as a blocked unschedule so the specific rule reads as the reason (#1526).
          this.errorState.set(
            mapped.issues.length > 0
              ? { ...mapped, title: this.hearingText.unscheduleBlockedTitle() }
              : mapped,
          );
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.mutatingProceedingId.set(null);
        if (result !== null) {
          // Re-fetch so the calendar + counts reflect the removal (AC4). The unschedule lands the
          // proceeding back in the 'continued' posture, which the ready-to-schedule projection
          // includes, so the backlog refresh returns the freed inmate to the Awaiting-scheduling
          // strip; the future-scheduled index refresh stops search treating them as scheduled.
          this.load();
          this.loadEligibleBacklog();
          this.loadFutureScheduledIndex();
        }
      });
  }

  /**
   * Scheduler-only: reschedule (change) an already-scheduled hearing's date, time, and/or location
   * (story 1445, AC1). Opens the Reschedule dialog prefilled from the row's current schedule; the
   * dialog posts to the dedicated reschedule endpoint (server-gated on workflow_runtime.transition
   * with the same slot-conflict validation as initial scheduling — the client gate is UX only) and
   * a durable schedule-history row records the prior + new windows (AC2). On success the calendar
   * recenters on the new date and reloads so all projection-backed surfaces reflect the change (AC3).
   */
  rescheduleHearing(row: ProceedingCalendarRow): void {
    if (
      !row ||
      !row.isScheduled ||
      !this.canMutateHearings() ||
      this.mutatingProceedingId() !== null
    ) {
      return;
    }

    const dialogRef = this.dialog.open<ScheduleHearingResult | undefined>(
      RescheduleHearingDialogComponent,
      {
        data: { hearing: row } satisfies RescheduleHearingDialogData,
        disableClose: true,
        panelClass: 'phms-reschedule-hearing-dialog',
        ariaLabel: this.hearingText.reschedule(),
      },
    );

    dialogRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) {
          return;
        }
        // Recenter the week view on the rescheduled date so the reload lands where the hearing
        // moved to and the change is immediately visible; then refresh the calendar + search index.
        const movedStart = result.scheduledStartUtc
          ? new Date(result.scheduledStartUtc)
          : null;
        if (movedStart && !Number.isNaN(movedStart.getTime())) {
          this.viewMode.set('calendar');
          this.calendarView.set(CalendarView.Week);
          this.viewDate.set(movedStart);
        }
        this.load();
        this.loadFutureScheduledIndex();
      });
  }

  /**
   * Commissioner-only: add (schedule) a ready-to-schedule candidate onto the calendar
   * (PHMS-SCH-6, AC1). Server authorization on workflow_runtime.transition is authoritative; the
   * client gate is UX only. On success it reloads so the new hearing appears and the counts
   * recompute (AC4).
   */
  addHearing(row: ProceedingCalendarRow, body: ScheduleHearingRequest): void {
    if (
      !row ||
      !this.canMutateHearings() ||
      this.mutatingProceedingId() !== null
    ) {
      return;
    }

    this.mutatingProceedingId.set(row.scheduledProceedingId);
    const save = row.schedulingContext && row.isScheduled
      ? this.scheduleService.rescheduleHearing(row.scheduledProceedingId, {...body,schedulingContext:row.schedulingContext})
      : this.scheduleService.scheduleHearing(row.scheduledProceedingId, row.schedulingContext ? {...body,schedulingContext:row.schedulingContext} : body);
    save.pipe(
        catchError((error: unknown) => {
          this.errorState.set(mapHearingScheduleError(error));
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.mutatingProceedingId.set(null);
        if (result !== null) {
          // Scheduling consumes a backlog candidate: refresh the strip so they leave Awaiting
          // scheduling, and the future-scheduled index so search sees the new hearing.
          this.load();
          this.loadEligibleBacklog();
          this.loadFutureScheduledIndex();
        }
      });
  }

  /**
   * Computes the visible {startUtc,endUtc} window for the active calendar view+date.
   * Returns null in List/grid mode so the existing range-agnostic fetch is used.
   */
  private currentRange(): CalendarRange | null {
    if (this.viewMode() !== 'calendar') {
      return null;
    }
    const date = this.viewDate();
    switch (this.calendarView()) {
      case CalendarView.Day:
        return dayRange(date);
      case CalendarView.Week:
        return weekRange(date);
      case CalendarView.Month:
      default:
        return monthRange(date);
    }
  }

  onSortButtonClick(button: ButtonConfig): void {
    const key = button.id?.startsWith('sort:')
      ? (button.id.slice('sort:'.length) as HearingScheduleSortKey)
      : null;
    if (key) {
      this.setSort(key);
    }
  }

  setSort(key: HearingScheduleSortKey): void {
    const current = this.sort();
    if (current.key === key) {
      this.sort.set({
        key,
        direction: current.direction === 'asc' ? 'desc' : 'asc',
      });
    } else {
      this.sort.set({ key, direction: 'asc' });
    }
  }

  load(explicitQuery?: HearingScheduleQuery): void {
    this.isLoading.set(true);
    this.errorState.set(null);
    // Clear prior rows so a failed reload never leaves stale hearings on screen.
    this.hearings.set([]);

    // List/calendar share one subscribe path: calendar mode overlays the visible
    // range onto the filter query (explicit dates from filters still win).
    const query = explicitQuery ?? this.buildQuery();

    this.scheduleService
      .getCalendar(query)
      .pipe(
        catchError((error: unknown) => {
          this.errorState.set(mapHearingScheduleError(error));
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((rows) => {
        this.hearings.set(rows ?? []);
        this.isLoading.set(false);
        // Success feedback for the toolbar's Refresh Schedule button (#1441): stamp the fetch time
        // only when the load succeeded (catchError sets errorState before emitting the null fallback).
        if (!this.errorState()) {
          this.lastUpdatedAt.set(new Date());
        }
      });
  }

  /**
   * Builds the calendar projection query: filter-derived params, with the active
   * calendar view's visible range filled in when the filters do not pin explicit
   * start/end dates. List/grid mode contributes no range.
   */
  private buildQuery(): HearingScheduleQuery {
    const query: HearingScheduleQuery = {};
    const range = this.currentRange();
    if (range) {
      query.startUtc = range.startUtc;
      query.endUtc = range.endUtc;
    }
    return query;
  }

  /**
   * Id of the proceeding whose hearing-detail flyout is open (#PHMS-SCH-7), or null when closed.
   * Clicking a calendar event/grid row opens the flyout (the new primary drill-in); the flyout's
   * own "View Full Hearing" action deep-links to the full detail page via the shared navigator.
   */
  readonly flyoutProceedingId = signal<number | null>(null);

  /** Return context the flyout's deep-link preserves for the detail screen's back affordance (981). */
  readonly flyoutReturnContext = computed(() => ({
    url: this.router.url,
    label: this.pageTitle,
  }));

  schedulingRequestFor(proceedingId:number): number | null {
    return this.hearings().find(r => r.scheduledProceedingId === proceedingId)?.schedulingContext?.requestId
      ?? this.futureScheduled().find(r => r.scheduledProceedingId === proceedingId)?.schedulingContext?.requestId ?? null;
  }
  openHearing(row: ProceedingCalendarRow): void {
    if (!row || !(row.scheduledProceedingId > 0)) {
      return;
    }
    // Open the hearing-detail flyout (#PHMS-SCH-7). The flyout fetches the proceeding-detail
    // projection + packet readiness and offers View Full Hearing / Upload Doc / Send Notice /
    // Reschedule; the full-page deep-link now lives behind the flyout's View Full Hearing action.
    this.flyoutProceedingId.set(row.scheduledProceedingId);
  }

  /**
   * Overdue-chip CTA (#1447 AC3): a past-date hearing awaiting its input deep-links straight to the
   * at-hearing input surface (`/phms/hearing-detail/{id}/at-hearing` — the outcome capture flow with
   * the decision gate) instead of the flyout, driving the user to record what happened. Recording
   * the input finalizes the hearing capture, which clears the derived overdue flag on the next read
   * of every surface (docs/phms/hearing-past-date-lifecycle.md).
   */
  recordHearingInput(row: ProceedingCalendarRow): void {
    if (!row || !(row.scheduledProceedingId > 0)) {
      return;
    }
    void this.router.navigate(
      hearingPhaseCommands(row.scheduledProceedingId, 'at'),
    );
  }

  closeFlyout(): void {
    this.flyoutProceedingId.set(null);
  }

  /**
   * Inmate-search Add-to-Calendar (coordinator/admin): open the inline date-picker dropdown seeded with
   * the candidate's default placement day. The coordinator picks the day to place the hearing on, which
   * triggers the schedule write (onAddDateSelected). Server authorization stays authoritative.
   */
  onSearchAdd(candidate: ReadyToScheduleRow): void {
    if (
      !candidate ||
      !this.canMutateHearings() ||
      this.mutatingProceedingId() !== null
    ) {
      return;
    }

    // #1487: a scheduling action opens the calendar on the week holding the earliest day this
    // hearing can actually be placed, instead of today — which for an unscheduled inmate is almost
    // always inside the minimum lead time, so the first placement attempt used to fail.
    const name =
      formatPersonNameLastFirst(candidate.title) ||
      this.subjectText.thisSubject();
    const firstAvailable = resolveFirstAvailableDate(candidate);
    if (!firstAvailable.date) {
      // No day satisfies the lead time and the due date together. Say so and leave the calendar
      // where the scheduler had it, rather than jumping to a date that cannot be scheduled.
      this.firstAvailable.set(null);
      this.scheduleNotice.set(
        `${name} cannot be scheduled: ${firstAvailable.blockedReason}.`,
      );
      return;
    }

    this.firstAvailable.set({ date: firstAvailable.date, name });
    this.setViewDate(firstAvailable.date);
    this.addDateDefault.set(resolveScheduleStartDefault(candidate));
    this.pendingAddCandidate.set(candidate);
  }

  /**
   * A day was chosen in the Add-to-Calendar dropdown: open the Schedule Hearing confirmation
   * dialog prefilled with that day so the coordinator confirms (or adjusts) the date, time, and
   * location before anything is written.
   */
  onAddDateSelected(day: Date): void {
    const candidate = this.pendingAddCandidate();
    if (!candidate || !(day instanceof Date) || Number.isNaN(day.getTime())) {
      return;
    }
    this.pendingAddCandidate.set(null);
    this.scheduleReadyCandidate(candidate, day);
  }

  /** Dismiss the Add-to-Calendar date-picker dropdown without scheduling. */
  cancelAddDate(): void {
    this.pendingAddCandidate.set(null);
  }

  /**
   * True while an eligible-strip card is being dragged. Drives the drop-target hover highlight on
   * the calendar day cells/columns (the CDK preview is pointer-events:none, so :hover lands on the
   * cell under the pointer while the class scopes the styling to an active drag).
   */
  readonly eligibleDragActive = signal(false);

  onEligibleDragStarted(): void {
    this.eligibleDragActive.set(true);
  }

  onEligibleDragEnded(): void {
    this.eligibleDragActive.set(false);
  }

  /**
   * An eligible-strip card was released. The strip's cdkDropList is connected to nothing (the
   * calendar grids are angular-calendar internals, not CDK lists), so every release lands back on
   * the source list and we resolve the calendar day under the pointer ourselves; releasing anywhere
   * that is not a calendar day is a no-op and the card animates home.
   */
  onEligibleDropped(event: CdkDragDrop<ReadyToScheduleRow[]>): void {
    this.eligibleDragActive.set(false);
    const candidate = event.item?.data as ReadyToScheduleRow | undefined;
    const dropPoint = event.dropPoint;
    if (!candidate || !dropPoint) {
      return;
    }
    const day = this.resolveDropDate(dropPoint.x, dropPoint.y);
    if (!day) {
      return;
    }
    this.dropScheduleCandidate(candidate, day);
  }

  /**
   * Drop-to-schedule: place a dragged eligible inmate on the dropped calendar day. Reuses the exact
   * Add-to-Calendar scheduling path (scheduleReadyCandidate — the same code the #1442 preselect and
   * the search tray's day picker run), so the Schedule Hearing confirmation dialog, the write, the
   * server-side conflict validation, the post-write reloads and the flyout open all behave
   * identically. Past days are blocked up front with the dismissible schedule notice (#1442
   * pattern) before the dialog opens — no dead server round-trip.
   */
  dropScheduleCandidate(candidate: ReadyToScheduleRow, day: Date): void {
    if (
      !candidate ||
      !this.canMutateHearings() ||
      this.mutatingProceedingId() !== null
    ) {
      return;
    }
    if (!(day instanceof Date) || Number.isNaN(day.getTime())) {
      return;
    }
    if (startOfDay(day).getTime() < startOfDay(new Date()).getTime()) {
      const name =
        formatPersonNameLastFirst(candidate.title) ||
        this.subjectText.thisSubject();
      this.toast.showError(
        `${name} cannot be scheduled on a past date. Drop them on today or a future day.`,
        SCHEDULE_BLOCKED_TITLE,
      );
      return;
    }
    // Client mirror of the server's lead-time rule (workflow.proceeding_schedule.lead_time_too_short,
    // ADO 1483): a drop inside the 15-day window is explained up front with the earliest allowed day
    // and the rule, in the same wording family the server returns. Checked before the weekend rule,
    // mirroring the server's guard order. The server stays authoritative for typed dialog dates.
    const leadTimeFloor = addDays(
      startOfDay(new Date()),
      SCHEDULE_MIN_LEAD_TIME_DAYS,
    );
    if (startOfDay(day).getTime() < leadTimeFloor.getTime()) {
      const name =
        formatPersonNameLastFirst(candidate.title) ||
        this.subjectText.thisSubject();
      this.toast.showError(
        `${name} cannot be scheduled until ${format(leadTimeFloor, 'MMM d, yyyy')} — hearings must be scheduled at least ${SCHEDULE_MIN_LEAD_TIME_DAYS} days out.`,
        SCHEDULE_BLOCKED_TITLE,
      );
      return;
    }
    // Client mirror of the server's weekend rule (workflow.proceeding_schedule.weekend_not_allowed):
    // block Saturday/Sunday drops up front with the same toast the past-date guard uses — no dialog,
    // no dead server round-trip. The server stays authoritative.
    if (isWeekend(day)) {
      const name =
        formatPersonNameLastFirst(candidate.title) ||
        this.subjectText.thisSubject();
      this.toast.showError(
        `${name} cannot be scheduled on a Saturday or Sunday.`,
        SCHEDULE_BLOCKED_TITLE,
      );
      return;
    }
    // Client mirror of the server's due-date rule (workflow.proceeding_schedule.after_due_date,
    // ADO 1513): a drop on a day after the candidate's Due Date / NLT (the ready-to-schedule row's
    // eligibilityNextEligibleDate) is explained up front, naming the due date. Day-granular: the due
    // day itself is allowed. Checked after the weekend rule, mirroring the server's guard order; a
    // candidate with no due date is not constrained. The server stays authoritative for typed dialog
    // dates and for drag-to-reschedule, whose calendar rows carry no due date.
    const dueDate = parseDateOnlyDay(schedulingDueDate(candidate));
    if (dueDate && startOfDay(day).getTime() > dueDate.getTime()) {
      const name =
        formatPersonNameLastFirst(candidate.title) ||
        this.subjectText.thisSubject();
      this.toast.showError(
        `${name} cannot be scheduled after ${format(dueDate, 'MMM d, yyyy')} — this hearing is due by that date.`,
        SCHEDULE_BLOCKED_TITLE,
      );
      return;
    }
    this.scheduleReadyCandidate(candidate, day);
  }

  /**
   * A scheduled month-view chip was released (#1458 chip-drag reschedule). Mirrors
   * onEligibleDropped: the chips' cdkDropList is connected to nothing, so every release lands back
   * on its source list and the calendar day under the pointer is resolved here; releasing anywhere
   * that is not a calendar day is a no-op and the chip animates home.
   */
  onScheduledChipDropped(event: CdkDragDrop<ProceedingCalendarRow[]>): void {
    this.eligibleDragActive.set(false);
    const hearing = event.item?.data as ProceedingCalendarRow | undefined;
    const dropPoint = event.dropPoint;
    if (!hearing || !dropPoint) {
      return;
    }
    const day = this.resolveDropDate(dropPoint.x, dropPoint.y);
    if (!day) {
      return;
    }
    this.dropRescheduleHearing(hearing, day);
  }

  /**
   * Drag-to-reschedule (#1458): move a dragged scheduled hearing to the dropped calendar day via
   * the EXISTING Reschedule dialog (rescheduleHearing — the same flow the List view's Reschedule
   * action runs), prefilled with the drop day while keeping the hearing's start time-of-day and
   * duration. Past and weekend days are blocked up front with the dismissible schedule notice
   * (the #1442 pattern) before the dialog opens — no dead server round-trip; the server's
   * weekend/lead-time rules stay authoritative for anything typed inside the dialog.
   */
  dropRescheduleHearing(hearing: ProceedingCalendarRow, day: Date): void {
    if (
      !hearing ||
      !hearing.isScheduled ||
      !this.canMutateHearings() ||
      this.mutatingProceedingId() !== null
    ) {
      return;
    }
    if (!(day instanceof Date) || Number.isNaN(day.getTime())) {
      return;
    }
    const name =
      formatPersonNameLastFirst(hearing.title) ||
      this.hearingText.thisHearing();
    if (startOfDay(day).getTime() < startOfDay(new Date()).getTime()) {
      this.toast.showError(
        `${name} cannot be rescheduled to a past date. Drop the hearing on today or a future day.`,
        RESCHEDULE_BLOCKED_TITLE,
      );
      return;
    }
    const leadTimeFloor = addDays(
      startOfDay(new Date()),
      SCHEDULE_MIN_LEAD_TIME_DAYS,
    );
    if (startOfDay(day).getTime() < leadTimeFloor.getTime()) {
      this.toast.showError(
        `${name} cannot be rescheduled before ${format(leadTimeFloor, 'MMM d, yyyy')} — hearings must be scheduled at least ${SCHEDULE_MIN_LEAD_TIME_DAYS} days out.`,
        RESCHEDULE_BLOCKED_TITLE,
      );
      return;
    }
    if (isWeekend(day)) {
      this.toast.showError(
        `${name} cannot be scheduled on a Saturday or Sunday.`,
        RESCHEDULE_BLOCKED_TITLE,
      );
      return;
    }
    this.rescheduleHearing(moveHearingToDay(hearing, day));
  }

  /**
   * Native angular-calendar drag on the Week/Day time grids (#1458): a scheduled event block
   * dragged to another day/slot lands here via (eventTimesChanged). Same contract as the month
   * chip-drag — past and weekend targets are blocked with the dismissible notice, and the move
   * only PREFILLS the existing Reschedule dialog (the dropped slot's exact start, duration
   * preserved when the grid supplies no end); nothing is written until the scheduler confirms
   * there. Until then the block snaps back because events() re-derives from the loaded rows.
   */
  onCalendarEventTimesChanged(
    change: CalendarEventTimesChangedEvent<ProceedingCalendarRow>,
  ): void {
    const hearing = change.event?.meta;
    const newStart = change.newStart;
    if (
      !hearing ||
      !hearing.isScheduled ||
      !this.canMutateHearings() ||
      this.mutatingProceedingId() !== null
    ) {
      return;
    }
    if (!(newStart instanceof Date) || Number.isNaN(newStart.getTime())) {
      return;
    }
    const name =
      formatPersonNameLastFirst(hearing.title) ||
      this.hearingText.thisHearing();
    if (startOfDay(newStart).getTime() < startOfDay(new Date()).getTime()) {
      this.toast.showError(
        `${name} cannot be rescheduled to a past date. Drop the hearing on today or a future day.`,
        RESCHEDULE_BLOCKED_TITLE,
      );
      return;
    }
    const leadTimeFloor = addDays(
      startOfDay(new Date()),
      SCHEDULE_MIN_LEAD_TIME_DAYS,
    );
    if (startOfDay(newStart).getTime() < leadTimeFloor.getTime()) {
      this.toast.showError(
        `${name} cannot be rescheduled before ${format(leadTimeFloor, 'MMM d, yyyy')} — hearings must be scheduled at least ${SCHEDULE_MIN_LEAD_TIME_DAYS} days out.`,
        RESCHEDULE_BLOCKED_TITLE,
      );
      return;
    }
    if (isWeekend(newStart)) {
      this.toast.showError(
        `${name} cannot be scheduled on a Saturday or Sunday.`,
        RESCHEDULE_BLOCKED_TITLE,
      );
      return;
    }
    this.rescheduleHearing(
      moveHearingToStart(hearing, newStart, change.newEnd ?? null),
    );
  }

  /**
   * Resolves the calendar day under a drop point across all three calendar views:
   * - Month: the custom day-cell template stamps its date as `data-drop-date`.
   * - Week: the pointer's `.cal-day-column` (or `.cal-header` day header) index maps onto
   *   startOfWeek(viewDate) + index — the same Sunday-start week both weekRange() and
   *   angular-calendar use.
   * - Day: the single-column week angular-calendar renders for the day view resolves to viewDate.
   * Returns null when the release point is not over a calendar day.
   */
  private resolveDropDate(x: number, y: number): Date | null {
    if (
      typeof document === 'undefined' ||
      typeof document.elementFromPoint !== 'function'
    ) {
      return null;
    }
    const target = document.elementFromPoint(x, y) as HTMLElement | null;
    if (!target) {
      return null;
    }

    const stamped = target
      .closest<HTMLElement>('[data-drop-date]')
      ?.getAttribute('data-drop-date');
    if (stamped) {
      const date = new Date(stamped);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const column = target.closest<HTMLElement>('.cal-day-column');
    if (column) {
      if (this.calendarView() === CalendarView.Day) {
        return startOfDay(this.viewDate());
      }
      const index = indexAmongClass(column, 'cal-day-column');
      return index >= 0 ? addDays(startOfWeek(this.viewDate()), index) : null;
    }

    const headerCell = target.closest<HTMLElement>(
      '.cal-day-headers .cal-header',
    );
    if (headerCell) {
      if (this.calendarView() === CalendarView.Day) {
        return startOfDay(this.viewDate());
      }
      const index = indexAmongClass(headerCell, 'cal-header');
      return index >= 0 ? addDays(startOfWeek(this.viewDate()), index) : null;
    }

    return null;
  }

  eligibleDateLabel(candidate: ReadyToScheduleRow): string {
    return formatDateOnlyLabel(candidate.schedulingContext?.dueDate ?? candidate.eligibilityNextEligibleDate) || '—';
  }

  eligibleLocationLabel(candidate: ReadyToScheduleRow): string {
    const parts = [candidate.locationName, candidate.roomName].filter(Boolean);
    return parts.length > 0 ? parts.join(' · ') : '—';
  }

  trackByEligible(_index: number, candidate: ReadyToScheduleRow): number {
    return candidate.scheduledProceedingId;
  }

  /**
   * Place an eligible candidate onto the calendar (PHMS-SCH-6, AC1) — via the Schedule Hearing
   * confirmation dialog, never by insta-booking. All three unscheduled-inmate entry points funnel
   * here (eligible-strip drag drop, dashboard Schedule deep-link, search-tray Add-to-Calendar day
   * picker), so every path gets the same confirm step: the dialog opens prefilled with the picked
   * day at the business-hours start (or the candidate's default placement day when no day was
   * picked) and the candidate's projected location/room; the scheduler adjusts date/time/location
   * and confirms. The dialog itself performs the schedule write (the same endpoint this method
   * used to call directly, with the dialog's explicit start/end instead of a hard-coded
   * dayStartHour window) and keeps conflict/permission errors in-dialog. Cancel makes no request
   * and leaves the calendar untouched.
   */
  private scheduleReadyCandidate(
    candidate: ReadyToScheduleRow,
    chosenDay?: Date,
  ): void {
    const availability = resolveFirstAvailableDate(candidate);
    if (availability.blockedReason) {
      const name = formatPersonNameLastFirst(candidate.title) || this.subjectText.thisSubject();
      this.scheduleNotice.set(`${name} cannot be scheduled: ${availability.blockedReason}.`);
      return;
    }
    const defaultDay = chosenDay ?? resolveAddStart(candidate);
    const defaultStart = atScheduleStartHour(defaultDay, this.dayStartHour);

    const dialogRef = this.dialog.open<ScheduleHearingResult | undefined>(
      ScheduleHearingDialogComponent,
      {
        data: { candidate, defaultStart } satisfies ScheduleHearingDialogData,
        disableClose: true,
        panelClass: 'phms-schedule-hearing-dialog',
        ariaLabel: this.hearingText.schedule(),
      },
    );

    dialogRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) {
          // Cancelled: the dialog made no request; calendar and backlog stay as they were.
          return;
        }
        // Recenter the WEEK view on the scheduled date so the post-write reload lands where the
        // new hearing sits and it is immediately visible (recenters land on Week — matching the
        // reschedule flow).
        const scheduledStart = result.scheduledStartUtc
          ? new Date(result.scheduledStartUtc)
          : defaultStart;
        if (!Number.isNaN(scheduledStart.getTime())) {
          this.viewMode.set('calendar');
          this.calendarView.set(CalendarView.Week);
          this.viewDate.set(scheduledStart);
        }
        this.load();
        // Refresh the eligible backlog so the just-scheduled inmate no longer offers "Add to Calendar",
        // and the forward index so it now offers "View in Calendar" instead.
        this.loadEligibleBacklog();
        this.loadFutureScheduledIndex();
        this.flyoutProceedingId.set(
          result.scheduledProceedingId || candidate.scheduledProceedingId,
        );
      });
  }

  /**
   * Remove-from-calendar requested from the flyout (#PHMS-SCH-7, gated on canMutateHearings). Routes to
   * the SCH-6 unschedule mutation so the hearing leaves the calendar; server authorization on
   * workflow_runtime.transition stays authoritative. Closes the flyout first so the subsequent reload
   * reflects the removal.
   */
  removeFromFlyout(scheduledProceedingId: number): void {
    const row = this.hearings().find(
      (item) => item.scheduledProceedingId === scheduledProceedingId,
    );
    this.closeFlyout();
    if (row) {
      this.removeHearing(row);
    }
  }

  /**
   * Move-date requested from the flyout (#PHMS-SCH-7, gated on canMutateHearings). Reschedules the
   * hearing in place to the new window via the SCH-6 schedule mutation, preserving the existing
   * location/room/format/slot; server authorization stays authoritative. Closes the flyout first so the
   * reload reflects the moved appointment.
   */
  moveDateFromFlyout(request: MoveDateRequest): void {
    const row = this.hearings().find(
      (item) => item.scheduledProceedingId === request.scheduledProceedingId,
    );
    this.closeFlyout();
    if (!row) {
      return;
    }
    // Recenter the week view on the new date (set directly, no extra fetch) so the post-write reload
    // in addHearing lands on the week the hearing moved to and the change is immediately visible.
    const movedStart = new Date(request.scheduledStartUtc);
    if (!Number.isNaN(movedStart.getTime())) {
      this.viewMode.set('calendar');
      this.calendarView.set(CalendarView.Week);
      this.viewDate.set(movedStart);
    }
    this.addHearing(row, {
      scheduledStartUtc: request.scheduledStartUtc,
      scheduledEndUtc: request.scheduledEndUtc,
      timeZoneId: row.timeZoneId,
      locationName: row.locationName ?? null,
      roomName: row.roomName ?? null,
      videoConferenceUrl: row.videoConferenceUrl ?? null,
      calendarSlotId: row.calendarSlotId ?? null,
    });
    // Keep the forward search index fresh so "View in Calendar" reflects the new month.
    this.loadFutureScheduledIndex();
  }

  formatStatus(statusCode: string | null | undefined): string {
    const normalized = (statusCode ?? '').trim();
    if (!normalized) {
      return 'Unknown';
    }
    return normalized
      .split(/[_\-\s]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private buildColumnDefs(): ColDef[] {
    return [
      {
        field: 'scheduledStartUtc',
        headerName: 'Date / Time',
        flex: 1,
        minWidth: 180,
        sortable: true,
        sort: 'asc',
        valueFormatter: ({ value }) =>
          formatDateTimeLabel(value as string | null | undefined),
      },
      {
        field: 'title',
        headerName: 'Last Name',
        flex: 0.8,
        minWidth: 130,
        sortable: true,
        // Renders the last name and sorts on the last-name key (#1436); the row keeps the raw title.
        valueFormatter: ({ value }) =>
          personLastName(value as string | null | undefined),
        comparator: (left, right) =>
          personLastNameKey(left as string | null | undefined).localeCompare(
            personLastNameKey(right as string | null | undefined),
          ),
      },
      {
        headerName: 'First Name',
        flex: 0.8,
        minWidth: 120,
        sortable: true,
        // Derived "First [Middle]" view of the same raw title.
        valueGetter: ({ data }) =>
          personFirstNames((data as ProceedingCalendarRow | undefined)?.title),
      },
      {
        field: 'proceedingNumber',
        headerName: this.hearingText.header(),
        width: 150,
        sortable: true,
      },
      {
        field: 'locationName',
        headerName: 'Location',
        flex: 1,
        minWidth: 150,
        sortable: true,
        valueFormatter: ({ value }) =>
          (value as string | null | undefined)?.trim() || '—',
      },
      {
        headerName: 'Resource',
        flex: 1,
        minWidth: 140,
        sortable: true,
        valueGetter: ({ data }) =>
          formatResource(data as ProceedingCalendarRow),
      },
      {
        field: 'schedulingStatusCode',
        headerName: this.operationalText.statusColumn(),
        width: 150,
        sortable: true,
        valueFormatter: ({ value }) =>
          this.formatStatus(value as string | null | undefined),
      },
      {
        headerName: '',
        width: 90,
        maxWidth: 90,
        sortable: false,
        cellRenderer: ButtonCellRenderer,
        cellRendererParams: {
          label: 'Open',
          onClick: (params: { rowData: ProceedingCalendarRow }) =>
            this.openHearing(params.rowData),
        },
      },
    ];
  }
}

/** Server permission gating hearing-official assignment writes (story 1444). */
const OFFICIAL_ASSIGN_PERMISSION = 'workflow_runtime.assign';

/**
 * Default placement day for an Add-to-Calendar: the hearing's first available date (#1487), so the
 * dialog prefills the same day the calendar opened on. This used to fall back to one week out, which
 * is inside the minimum lead time and therefore a date the server refuses.
 */
function resolveAddStart(candidate: ReadyToScheduleRow): Date {
  return resolveScheduleStartDefault(candidate);
}

/**
 * Combines a calendar day chosen by drop/day-pick with the business-day start hour, yielding the
 * DEFAULT start prefilled into the Schedule Hearing confirmation dialog (the scheduler edits the
 * actual time there before confirming).
 */
function atScheduleStartHour(day: Date, startHour: number): Date {
  return new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    startHour,
    0,
    0,
    0,
  );
}

/**
 * Copy of a scheduled row moved to `day`, preserving the hearing's local start time-of-day and its
 * duration (#1458 chip-drag reschedule). The copy only PREFILLS the existing Reschedule dialog —
 * nothing is written until the scheduler confirms there — so the original row stays untouched.
 */
function moveHearingToDay(
  hearing: ProceedingCalendarRow,
  day: Date,
): ProceedingCalendarRow {
  const start = hearing.scheduledStartUtc
    ? new Date(hearing.scheduledStartUtc)
    : null;
  if (!start || Number.isNaN(start.getTime())) {
    return hearing;
  }
  const movedStart = new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    start.getHours(),
    start.getMinutes(),
    0,
    0,
  );
  const end = hearing.scheduledEndUtc
    ? new Date(hearing.scheduledEndUtc)
    : null;
  const movedEnd =
    end && !Number.isNaN(end.getTime())
      ? new Date(movedStart.getTime() + (end.getTime() - start.getTime()))
      : null;
  return {
    ...hearing,
    scheduledStartUtc: movedStart.toISOString(),
    scheduledEndUtc: movedEnd
      ? movedEnd.toISOString()
      : hearing.scheduledEndUtc,
  };
}

/**
 * Copy of a scheduled row moved to the exact `newStart` dropped on a Week/Day time grid (#1458),
 * keeping the original duration when the grid supplies no end. Like moveHearingToDay, the copy
 * only PREFILLS the Reschedule dialog — the original row stays untouched until confirmation.
 */
function moveHearingToStart(
  hearing: ProceedingCalendarRow,
  newStart: Date,
  newEnd: Date | null,
): ProceedingCalendarRow {
  const start = hearing.scheduledStartUtc
    ? new Date(hearing.scheduledStartUtc)
    : null;
  const end = hearing.scheduledEndUtc
    ? new Date(hearing.scheduledEndUtc)
    : null;
  const movedEnd =
    newEnd && !Number.isNaN(newEnd.getTime())
      ? newEnd
      : start &&
          end &&
          !Number.isNaN(start.getTime()) &&
          !Number.isNaN(end.getTime())
        ? new Date(newStart.getTime() + (end.getTime() - start.getTime()))
        : null;
  return {
    ...hearing,
    scheduledStartUtc: newStart.toISOString(),
    scheduledEndUtc: movedEnd
      ? movedEnd.toISOString()
      : hearing.scheduledEndUtc,
  };
}

function formatDateTimeLabel(value: string | null | undefined): string {
  if (!value) {
    return 'Unscheduled';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(date);
}

/**
 * A date-only server value ("2026-11-07") as the LOCAL calendar day it names, so it compares with
 * the local drop day (startOfDay) without a UTC-midnight shift; null when absent/invalid.
 */
function parseDateOnlyDay(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : startOfDay(date);
}

/** Date-only label ("Jan 5, 2027") for the eligible-strip meta line; null when absent/invalid. */
function formatDateOnlyLabel(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const date = parseDateOnlyDay(value);
  return date
    ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(date)
    : null;
}

/**
 * Index of `element` among its siblings that carry `className` (angular-calendar renders week/day
 * columns and header cells as same-class siblings in day order). -1 when detached or not found.
 */
function indexAmongClass(element: HTMLElement, className: string): number {
  const siblings = Array.from(element.parentElement?.children ?? []).filter(
    (sibling) => sibling.classList.contains(className),
  );
  return siblings.indexOf(element);
}
